import { DestroyRef, effect, Injectable, inject } from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import type { HubConnection, IHttpConnectionOptions } from '@microsoft/signalr'
import { HubConnectionBuilder, HubConnectionState, LogLevel } from '@microsoft/signalr'
import { apiEndpoint, isLiveMobile } from '@awork/environments/environment'
import type { Observable } from 'rxjs'
import { BehaviorSubject, Subject, filter, map } from 'rxjs'
import type {
  ExcludedSignal,
  WebSocketSignalEntityStore,
  WebSocketSignalEntityType
} from '@awork/_shared/services/signal-service/signal.model'
import { WebSocketSignal } from '@awork/_shared/services/signal-service/signal.model'
import type { TimeTracking } from '@awork/features/time-tracking/models/time-tracking.model'
import type { TimeTrackingStore } from '@awork/features/time-tracking/state/time-tracking.store'
import type { Project } from '@awork/features/project/models/project.model'
import type { ProjectStore } from '@awork/features/project/state/project.store'
import type { ProjectStatus } from '@awork/features/project/models/project-status.model'
import type { ProjectStatusStore } from '@awork/features/project/state/project-status.store'
import type { Task } from '@awork/features/task/models/task.model'
import type { TaskStore } from '@awork/features/task/state/task.store'
import type { TaskStatusStore } from '@awork/features/task/state/task-status.store'
import type { TaskListStore } from '@awork/features/task/state/task-list.store'
import type { ChecklistItemStore } from '@awork/features/task/state/checklist-item.store'
import type { User } from '@awork/features/user/models/user.model'
import type { UserStore } from '@awork/features/user/state/user.store'
import { UserQuery } from '@awork/features/user/state/user.query'
import type { Company } from '@awork/features/company/models/company.model'
import type { CompanyStore } from '@awork/features/company/state/company.store'
import { AppQuery } from '@awork/core/state/app.query'
import type { TaskList } from '@awork/features/task/models/task-list.model'
import type { TaskStatus } from '@awork/features/task/models/task-status.model'
import type { NotificationStore } from '@awork/framework/state/notification.store'
import type { Notification } from '@awork/framework/models/notification.model'
import { AccountQuery } from '@awork/_shared/state/account.query'
import type { ChecklistItem } from '@awork/features/task/models/checklist-item'
import type { Absence } from '@awork/features/user/models/absence.model'
import type { TimeBookingStore } from '@awork/features/planner/state/time-booking.store'
import type { TimeBooking } from '@awork/features/planner/models/time-booking.model'
import type { DocumentStore } from '@awork/features/document/state/document.store'
import type { Document } from '@awork/features/document/models/document.model'
import type { DocumentSpaceStore } from '@awork/features/document/state/document-space.store'
import type { DocumentSpace } from '@awork/features/document/models/document-space.model'
import type { WorkTypeStore } from '@awork/features/task/state/work-type.store'
import type { WorkType } from '@awork/features/task/models/work-type.model'
import type { TaskScheduleStore } from '@awork/features/planner/state/task-schedule.store'
import type { TaskSchedule } from '@awork/features/planner/models/task-schedule.model'
import type { CommentStore } from '@awork/_shared/state/comment.store'
import { Comment } from '@awork/_shared/models/comment.model'
import isTest from '@awork/_shared/functions/is-test'
import type { Workflow } from '@awork/features/workflow/models/workflow.model'
import type { WorkflowStore } from '@awork/features/workflow/state/workflow.store'
import type { EntityFilterStore } from '@awork/_shared/state/entity-filter.store'
import type { FilterEntity } from '@awork/_shared/models/filters.model'
import { ApiClient } from '@awork/_shared/services/api-client/ApiClient'
import { ApiCacheService } from '@awork/_shared/services/api-cache-service/api-cache.service'
import type { Agent } from '@awork/features/agent/models/agent.model'
import type { AgentSkill } from '@awork/features/agent/models/agent-skill.model'
import type { AgentConnector } from '@awork/features/agent/services/agent-service/types'
import type { AgentStore } from '@awork/features/agent/state/agent.store'
import type { AgentSkillStore } from '@awork/features/agent/state/agent-skill.store'
import type { AgentConnectorStore } from '@awork/features/agent/state/agent-connector.store'
import { WorkspaceQuery } from '@awork/features/workspace/state/workspace.query'

export type SignalConnectionState = 'connecting' | 'connected' | 'disconnected'

export interface SignalRealtimeDiagnostics {
  connectionId: string | null
  heartbeatAgeMs: number | null
  host: string
  hubPath: string
  lastError: string | null
  lastHeartbeatAt: string | null
  reconnectCount: number
  state: SignalConnectionState
}

@Injectable({
  providedIn: 'root'
})
export class SignalService {
  private appQuery = inject(AppQuery)
  private accountQuery = inject(AccountQuery)
  private readonly accountTimezone = this.accountQuery.queryTimezone()
  private userQuery = inject(UserQuery)
  private workspaceQuery = inject(WorkspaceQuery)
  private apiClient = inject(ApiClient)
  private apiCacheService = inject(ApiCacheService)
  private destroyRef = inject(DestroyRef)

  private readonly url: string
  private readonly pingIntervalMs = 60000
  private hubConnection: HubConnection
  private signalReceived = new Subject<WebSocketSignal>()
  private lastHeartbeatAtSubject = new BehaviorSubject<number | null>(null)
  private connectionStateSubject = new BehaviorSubject<SignalConnectionState>('disconnected')
  private reconnectCountSubject = new BehaviorSubject<number>(0)
  private lastErrorSubject = new BehaviorSubject<string | null>(null)
  private startTimeout: number
  private connectionGeneration = 0
  private pingInterval: number
  private registered = false
  private readonly maxBufferedSignalsPerTrace = 100
  private readonly bufferedSignalsByTraceId = new Map<string, WebSocketSignal[]>()
  private readonly bufferedTraceByEntity = new Map<string, string>()
  private readonly bypassedSignalTraceIds = new Set<string>()
  private signalCorrelationSessionKey: string = null

  connectionId: string = null
  connected = new Subject<void>()
  reconnected = new Subject<void>()
  disconnected = new Subject<void>()
  readonly lastHeartbeat$ = this.lastHeartbeatAtSubject.asObservable()
  readonly connectionState$ = this.connectionStateSubject.asObservable()
  readonly reconnectCount$ = this.reconnectCountSubject.asObservable()
  readonly lastError$ = this.lastErrorSubject.asObservable()

  failConnectionDelay = 10000
  retryConnectionDelay = 500
  maxRetryConnectionDelay = 5 * 60000 // 5 minutes

  private excludedSignals: ExcludedSignal[] = []
  private temporaryExcludedSignals: ExcludedSignal[] = []
  private currentUser = this.userQuery.queryCurrentUser()
  private currentWorkspace = this.workspaceQuery.queryCurrentWorkspace()

  constructor() {
    this.url = `${apiEndpoint}/notifications/websocketnotifications/`
    this.reconnected.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => this.handleWebsocketReconnection())
    this.apiClient.signalTraceSettled$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(traceId => this.releaseBufferedSignals(traceId))

    effect(() => {
      const isLoggedIn = this.appQuery.query('isLoggedIn')
      const sessionKey = isLoggedIn() ? `${this.currentWorkspace()?.id ?? ''}:${this.currentUser()?.id ?? ''}` : null

      if (sessionKey !== this.signalCorrelationSessionKey) {
        this.clearSignalCorrelationState()
        this.signalCorrelationSessionKey = sessionKey
      }
    })

    effect(() => {
      const isLoggedIn = this.appQuery.query('isLoggedIn')

      if (isTest()) {
        return
      }

      if (isLoggedIn() && (!this.hubConnection || this.hubConnection.state === HubConnectionState.Disconnected)) {
        this.startTimeout = window.setTimeout(() => {
          this.createConnection()
          this.startConnection()
        }, 300)
      } else if (!isLoggedIn()) {
        this.stopConnection()
        clearTimeout(this.startTimeout)
        clearInterval(this.pingInterval)
        this.resetHeartbeat()
      }
    })
  }

  /**
   * Creates the signal connection
   */
  private createConnection(): void {
    // Stop (close) any open connection before creating a new one (this should not happen)
    this.stopConnection()

    let accessToken: string

    if (isLiveMobile) {
      accessToken = this.accountQuery.getAccount().accessToken
    }

    let url: string
    let options: IHttpConnectionOptions

    if (isLiveMobile) {
      url = `${this.url}?aw-mobile=true&jwt=${accessToken}`
    } else {
      url = this.url
      options = { accessTokenFactory: () => accessToken }
    }

    const hubConnectionBuilder = new HubConnectionBuilder()
      .withUrl(url, options)
      .withAutomaticReconnect(this.getRetryDelays())

    const connection = hubConnectionBuilder.configureLogging(LogLevel.None).build()
    const generation = this.connectionGeneration

    this.hubConnection = connection

    connection.onclose(() => {
      if (generation !== this.connectionGeneration) {
        return
      }

      this.connectionStateSubject.next('disconnected')
      this.disconnected.next()
    })

    connection.on('Ping', () => {
      if (generation === this.connectionGeneration) {
        this.markHeartbeat()
      }
    })
    connection.on('Pong', () => {
      if (generation === this.connectionGeneration) {
        this.markHeartbeat()
      }
    })

    connection.onreconnected(() => {
      if (generation !== this.connectionGeneration) {
        return
      }

      this.reconnected.next()
      this.connectionStateSubject.next('connected')
      this.reconnectCountSubject.next(this.reconnectCountSubject.value + 1)
      this.lastErrorSubject.next(null)
      this.connectionId = connection.connectionId
      this.markHeartbeat()
    })
  }

  /**
   * Defines the reconnection retry delays
   * The delay increases exponentially until it reaches the maximum delay, in total it lasts for 24h approx.
   * @returns {number[]}
   */
  private getRetryDelays(): number[] {
    return new Array<number>(300).fill(0).map((ms: number, i: number) => {
      const expValue = this.retryConnectionDelay * Math.pow(i + 1, 2)
      return expValue < this.maxRetryConnectionDelay ? expValue : this.maxRetryConnectionDelay
    })
  }

  /**
   * Starts the signal connection
   * @param {number} [delay=300]
   */
  private startConnection(delay = 300): void {
    const connection = this.hubConnection
    const generation = this.connectionGeneration

    this.connectionStateSubject.next('connecting')
    this.startTimeout = window.setTimeout(() => {
      if (generation !== this.connectionGeneration) {
        return
      }

      connection
        .start()
        .then(
          () => {
            if (generation !== this.connectionGeneration) {
              return
            }

            this.connected.next()
            this.connectionStateSubject.next('connected')
            this.lastErrorSubject.next(null)
            this.registerOnSignalEvents()
            this.sendPings()
            this.connectionId = this.hubConnection.connectionId
            this.markHeartbeat()

            // Reset delays
            this.failConnectionDelay = 10000
            this.retryConnectionDelay = 500
          },
          () => {
            if (generation !== this.connectionGeneration) {
              return
            }

            this.lastErrorSubject.next('Failed to start websocket notifications connection')
            this.createConnection()
            this.startConnection(this.failConnectionDelay)
            this.failConnectionDelay = this.failConnectionDelay * 2
          }
        )
        .catch(() => {
          if (generation === this.connectionGeneration) {
            this.lastErrorSubject.next('Unexpected websocket notifications connection error')
          }
        })
    }, delay)
  }

  /**
   * Stops the signal connection
   */
  private stopConnection(): void {
    // Invalidate callbacks before stop() can reject an unfinished start.
    this.connectionGeneration++

    if (this.hubConnection) {
      this.hubConnection.stop().catch(() => {})
      this.connectionStateSubject.next('disconnected')

      // Resetting the flag so the new connection's 'on' event can be registered
      this.registered = false
    }
  }

  /** Stops live updates and pending connection work before replacing the workspace context. */
  disconnect(): void {
    this.stopConnection()
    this.connectionId = null
    clearTimeout(this.startTimeout)
    clearInterval(this.pingInterval)
    this.resetHeartbeat()
  }

  /** Starts live updates with the current workspace's confirmed session. */
  reconnect(): void {
    this.disconnect()
    this.createConnection()
    this.startConnection()
  }

  /**
   * Registers to signal events
   * Note: We need to register just once, even if a reconnection occurs
   * @param {string} [name='EntityChange']
   */
  private registerOnSignalEvents(name = 'EntityChange'): void {
    if (!this.registered) {
      const generation = this.connectionGeneration

      this.hubConnection.on(name, (data: WebSocketSignal) => {
        if (generation !== this.connectionGeneration) {
          return
        }

        this.markHeartbeat()
        this.handleIncomingSignal(data)
      })

      this.registered = true
    }
  }

  /**
   * Buffers a signal for a pending frontend trace or processes it immediately.
   * @param {WebSocketSignal} signal
   */
  protected handleIncomingSignal(signal: WebSocketSignal): void {
    const entityKey = `${signal.entityName}:${signal.entityId}`
    const blockingTraceId = this.bufferedTraceByEntity.get(entityKey)
    const traceId = blockingTraceId ?? signal.traceId?.toLowerCase()

    if (
      !blockingTraceId &&
      (!traceId || !this.apiClient.isSignalTracePending(traceId) || this.bypassedSignalTraceIds.has(traceId))
    ) {
      this.processIncomingSignal(signal)

      return
    }

    const bufferedSignals = this.bufferedSignalsByTraceId.get(traceId) ?? []

    if (bufferedSignals.length >= this.maxBufferedSignalsPerTrace) {
      const signalsToRelease = [...bufferedSignals, signal]

      this.removeBufferedSignals(traceId)
      this.bypassedSignalTraceIds.add(traceId)
      signalsToRelease.forEach(bufferedSignal => this.processIncomingSignal(bufferedSignal))

      return
    }

    this.bufferedSignalsByTraceId.set(traceId, [...bufferedSignals, signal])
    this.bufferedTraceByEntity.set(entityKey, traceId)
  }

  /**
   * Replays all buffered signals for a settled trace through the normal signal flow.
   * @param {string} traceId
   */
  private releaseBufferedSignals(traceId: string): void {
    const bufferedSignals = this.removeBufferedSignals(traceId)

    this.bypassedSignalTraceIds.delete(traceId)
    // A later request for the same entity may still be pending when this one settles.
    bufferedSignals.forEach(signal => this.handleIncomingSignal(signal))
  }

  /**
   * Removes a trace buffer and its entity ordering entries before replay.
   * @param {string} traceId
   * @returns {WebSocketSignal[]}
   */
  private removeBufferedSignals(traceId: string): WebSocketSignal[] {
    const bufferedSignals = this.bufferedSignalsByTraceId.get(traceId) ?? []

    this.bufferedSignalsByTraceId.delete(traceId)
    bufferedSignals.forEach(signal => {
      const entityKey = `${signal.entityName}:${signal.entityId}`

      if (this.bufferedTraceByEntity.get(entityKey) === traceId) {
        this.bufferedTraceByEntity.delete(entityKey)
      }
    })

    return bufferedSignals
  }

  /**
   * Discards buffered signals and request traces from the previous authenticated session.
   */
  private clearSignalCorrelationState(): void {
    this.bufferedSignalsByTraceId.clear()
    this.bufferedTraceByEntity.clear()
    this.bypassedSignalTraceIds.clear()
    this.apiClient.clearSignalTraceState()
  }

  /**
   * Applies exclusions and emits a signal through the existing websocket flow.
   * @param {WebSocketSignal} signal
   */
  private processIncomingSignal(signal: WebSocketSignal): void {
    if (!this.isExcludedSignal(signal)) {
      this.signalReceived.next(signal)
    }
  }

  /**
   * Checks if the signal should be excluded.
   * It checks if the signal was triggered by the current user
   * and if the entity and property names in the change list match the excluded ones.
   * @param {WebSocketSignal} signal
   * @returns {boolean}
   */
  isExcludedSignal(signal: WebSocketSignal): boolean {
    const changes = signal.changes ?? []

    if (this.isExcludedByTraceId(signal, changes)) {
      return true
    }

    if (signal.createdBy !== this.currentUser()?.id) {
      return false
    }

    this.removeExpiredTemporaryExcludedSignals()

    return [...this.excludedSignals, ...this.temporaryExcludedSignals].some(
      excludedSignal =>
        signal.entityName === excludedSignal.entityName &&
        (!excludedSignal.entityId || signal.entityId === excludedSignal.entityId) &&
        this.hasMatchingPropertyChange(changes, excludedSignal.propertyName)
    )
  }

  /**
   * Checks if a signal matches entity and property names registered for its trace id.
   * @param {WebSocketSignal} signal
   * @param {{ property: string; }[]} changes
   * @returns {boolean}
   */
  private isExcludedByTraceId(signal: WebSocketSignal, changes: { property: string }[]): boolean {
    if (!signal.traceId) {
      return false
    }

    const exclusion = this.apiClient.getSignalExclusionForTraceId(signal.traceId)

    if (!exclusion || signal.entityName.toLowerCase() !== exclusion.entityName.toLowerCase()) {
      return false
    }

    return this.hasMatchingPropertyChange(changes, exclusion.propertyName)
  }

  /**
   * Checks if one of the changes contains the property name case-insensitively.
   * @param {{ property: string; }[]} changes
   * @param {string} propertyName
   * @returns {boolean}
   */
  private hasMatchingPropertyChange(changes: { property: string }[], propertyName: string): boolean {
    return changes.some(change => {
      return change.property.toLowerCase().includes(propertyName.toLowerCase())
    })
  }

  /**
   * Sends a ping periodically to keep the connection alive and track websocket health.
   */
  private sendPings(): void {
    if (this.pingInterval) {
      clearInterval(this.pingInterval)
    }

    this.pingInterval = window.setInterval(() => {
      if (this.hubConnection && this.hubConnection.state === HubConnectionState.Connected) {
        this.hubConnection
          .send('Ping')
          .then(() => this.markHeartbeat())
          .catch(() => {
            // Ping failures are expected during reconnection and handled by SignalR
          })
      }
    }, this.pingIntervalMs)
  }

  /**
   * Updates the latest successful heartbeat timestamp.
   */
  private markHeartbeat(): void {
    this.lastHeartbeatAtSubject.next(Date.now())
  }

  /**
   * Clears the heartbeat state.
   */
  private resetHeartbeat(): void {
    this.lastHeartbeatAtSubject.next(null)
  }

  /**
   * Invalidates cached API responses when the websocket reconnects.
   */
  private handleWebsocketReconnection(): void {
    this.apiCacheService.invalidate({ all: true })
  }

  /**
   * Returns a snapshot of websocket diagnostics for support exports.
   * @param {number} nowMs
   * @returns {SignalRealtimeDiagnostics}
   */
  getRealtimeDiagnosticsSnapshot(nowMs: number): SignalRealtimeDiagnostics {
    const hubUrl = new URL(this.url, window.location.origin)
    const heartbeatTimestamp = this.lastHeartbeatAtSubject.value

    return {
      state: this.connectionStateSubject.value,
      connectionId: this.connectionId ?? null,
      reconnectCount: this.reconnectCountSubject.value,
      lastError: this.lastErrorSubject.value,
      host: hubUrl.host,
      hubPath: hubUrl.pathname,
      lastHeartbeatAt: heartbeatTimestamp ? new Date(heartbeatTimestamp).toISOString() : null,
      heartbeatAgeMs: heartbeatTimestamp ? nowMs - heartbeatTimestamp : null
    }
  }

  /**
   * Gets an observable of signal of the specified entity
   * @param {WebSocketSignalEntityType} entityType
   * @param {WebSocketSignalEntityStore} store The store that needs to be updated
   * @param {Subject<string>} deleteSubject Subject used for delete operations
   * @returns {Observable<WebSocketSignal>}
   */
  getSignal(
    entityType: WebSocketSignalEntityType,
    store?: WebSocketSignalEntityStore,
    deleteSubject?: Subject<string>
  ): Observable<WebSocketSignal> {
    const signal$ = this.signalReceived.pipe(
      filter(signal => signal.entityName === entityType),
      map(signal => new WebSocketSignal(signal, this.userQuery.getCurrentUser()))
    )

    if (store || deleteSubject) {
      signal$.subscribe(signal => {
        // If a store is specified, update it with the signal's data
        if (store) {
          this.processSignal(signal, store)
        }

        // If the delete subject is specified and is a deleted operation, emit the deleted entity id
        if (deleteSubject && signal.operationType === 'deleted') {
          deleteSubject.next(signal.entityId)
        }
      })
    }

    return signal$
  }

  /**
   * Updates the store according to the signal received
   * @param {WebSocketSignal} signal
   * @param {WebSocketSignalEntityStore} store
   */
  private processSignal(signal: WebSocketSignal, store: WebSocketSignalEntityStore): void {
    switch (signal.operationType) {
      case 'added':
      case 'updated':
        this.syncStore(signal, store)
        break
      case 'deleted':
        this.deleteStoreEntity(signal, store)
        break
    }
  }

  /**
   * Syncs the store with the signal's data
   * @param {WebSocketSignal} signal
   * @param {WebSocketSignalEntityStore} store
   */
  private deleteStoreEntity(signal: WebSocketSignal, store: WebSocketSignalEntityStore): void {
    switch (signal.entityName) {
      case 'absence': {
        store = store as UserStore

        const absence = signal.entity as Absence

        store.deleteAbsence(absence)
        break
      }
      default:
        // A websocket delete is server-confirmed, so the entity also leaves every sync filter
        store.removeDeleted(signal.entityId)
        break
    }
  }

  /**
   * Syncs the store according to the signal's entity type
   * @param {WebSocketSignal} signal
   * @param {WebSocketSignalEntityStore} store
   */
  private syncStore(signal: WebSocketSignal, store: WebSocketSignalEntityStore): void {
    switch (signal.entityName) {
      case 'project': {
        store = store as ProjectStore

        const updatedProject = signal.entity as Project

        updatedProject.imageLastUpdatedOn = new Date()
        updatedProject.workflowId = updatedProject.workflowId ?? null

        store.syncEntity(updatedProject)
        break
      }
      case 'task':
        store = store as TaskStore
        store.syncEntity(signal.entity as Task)
        break
      case 'timetracking':
        store = store as TimeTrackingStore
        store.syncEntity((signal.entity as TimeTracking).withTimezone(this.accountTimezone()))
        break
      case 'user':
        store = store as UserStore

        if (this.isArchivedUserSignalEntity(signal.entity as User)) {
          store.remove(signal.entityId)
          break
        }

        store.syncEntity(signal.entity as User)
        break
      case 'company':
        store = store as CompanyStore
        store.syncEntity(signal.entity as Company)
        break
      case 'tasklist':
        store = store as TaskListStore
        store.syncEntity(signal.entity as TaskList)
        break
      case 'taskstatus':
        store = store as TaskStatusStore
        store.syncEntity(signal.entity as TaskStatus)
        break
      case 'projectstatus':
        store = store as ProjectStatusStore
        store.syncEntity(signal.entity as ProjectStatus)
        break
      case 'checklistitem':
        store = store as ChecklistItemStore
        store.syncEntity(signal.entity as ChecklistItem)
        break
      case 'notification':
        store = store as NotificationStore
        store.syncEntity(signal.entity as Notification)
        break
      case 'absence': {
        store = store as UserStore

        const absence = signal.entity as Absence

        store.syncAbsence(absence)
        break
      }
      case 'projecttimebooking':
        store = store as TimeBookingStore
        store.syncEntity(signal.entity as TimeBooking)
        break
      case 'document':
        store = store as DocumentStore
        store.syncEntity(signal.entity as Document)
        break
      case 'documentspace':
        store = store as DocumentSpaceStore
        store.syncEntity(signal.entity as DocumentSpace)
        break
      case 'typeofwork':
        store = store as WorkTypeStore
        store.syncEntity(signal.entity as WorkType)
        break
      case 'taskschedule':
        store = store as TaskScheduleStore
        store.syncEntity(signal.entity as TaskSchedule)
        break
      case 'comment': {
        store = store as CommentStore

        const comment = signal.entity as Comment
        const previousComment = store.entityMap()[comment.id]
        const hasAttachmentChange = signal.changes?.some(change => change.property.toLowerCase().includes('attachment'))

        // Websocket comments omit resolved files, but the Comment constructor defaults them to [].
        // Keep existing files while the message that references them is unchanged.
        if (
          previousComment &&
          previousComment.message === comment.message &&
          !hasAttachmentChange &&
          !comment.attachments?.length
        ) {
          store.syncEntity(new Comment({ ...comment, attachments: previousComment.attachments }))
        } else {
          store.syncEntity(comment)
        }

        break
      }
      case 'workflow':
        store = store as WorkflowStore
        store.syncEntity(signal.entity as Workflow)
        break
      case 'filter':
        store = store as EntityFilterStore
        store.syncEntity(signal.entity as FilterEntity)
        break
      case 'agent':
        store = store as AgentStore
        store.syncEntity(signal.entity as Agent)
        break
      case 'agentskill':
        store = store as AgentSkillStore
        store.syncEntity(signal.entity as AgentSkill)
        break
      case 'agentconnector':
        store = store as AgentConnectorStore
        store.syncEntity(signal.entity as AgentConnector)
        break
    }
  }

  /**
   * Excludes a signal from being processed.
   * A signal is excluded if it was triggered by the current user and matches the excluded entity and property names.
   * @param {{ entityName: WebSocketSignalEntityType; propertyName: string; }} excludedSignal
   */
  excludeSignal(excludedSignal: { entityName: WebSocketSignalEntityType; propertyName: string }): void {
    this.excludedSignals.push(excludedSignal)
  }

  /**
   * Excludes multiple signals temporarily from being processed.
   * A signal is excluded if it was triggered by the current user, matches the entity and property names,
   * optionally the entity id, and is received before the exclusion expires.
   * @param {Omit<ExcludedSignal, 'expiresAt'>[]} excludedSignals
   * @param {number} durationMs
   */
  temporarilyExcludeSignals(excludedSignals: Omit<ExcludedSignal, 'expiresAt'>[], durationMs: number): void {
    const expiresAt = Date.now() + durationMs

    this.temporaryExcludedSignals.push(
      ...excludedSignals.map(excludedSignal => ({
        ...excludedSignal,
        expiresAt
      }))
    )
  }

  /**
   * Removes the given temporary exclusions immediately.
   * @param {Omit<ExcludedSignal, 'expiresAt'>[]} excludedSignals
   */
  removeTemporaryExcludedSignals(excludedSignals: Omit<ExcludedSignal, 'expiresAt'>[]): void {
    this.temporaryExcludedSignals = this.temporaryExcludedSignals.filter(temporaryExcludedSignal => {
      return !excludedSignals.some(excludedSignal =>
        this.isMatchingExcludedSignal(temporaryExcludedSignal, excludedSignal)
      )
    })
  }

  /**
   * Returns true when an incoming user signal entity is archived.
   * Handles both camelCase and PascalCase API payloads.
   * @param {User} user
   * @returns {boolean}
   */
  private isArchivedUserSignalEntity(user: User): boolean {
    const userWithPascalArchive = user as User & { IsArchived?: boolean }
    return userWithPascalArchive.isArchived ?? userWithPascalArchive.IsArchived ?? false
  }

  /**
   * Removes expired temporary exclusions.
   */
  private removeExpiredTemporaryExcludedSignals(): void {
    const now = Date.now()

    this.temporaryExcludedSignals = this.temporaryExcludedSignals.filter(excludedSignal => {
      return !excludedSignal.expiresAt || excludedSignal.expiresAt > now
    })
  }

  /**
   * Checks if the provided exclusions refer to the same signal target.
   * @param {ExcludedSignal} left
   * @param {Omit<ExcludedSignal, 'expiresAt'>} right
   * @returns {boolean}
   */
  private isMatchingExcludedSignal(left: ExcludedSignal, right: Omit<ExcludedSignal, 'expiresAt'>): boolean {
    return (
      left.entityName === right.entityName &&
      left.propertyName === right.propertyName &&
      left.entityId === right.entityId
    )
  }
}
